import { accountFeeType } from "constants/fee";
import { orderTypeConstants } from "constants/order";

export function getFeeTypeForOrder(orderType: string): string | null {
  switch (orderType) {
    case orderTypeConstants.PATIENT_RESPONSIBILITY:
      return accountFeeType.CTFForPR;
    case orderTypeConstants.BUNDLED:
      return accountFeeType.ClearTransactionFee;
    case orderTypeConstants.GFE:
      return accountFeeType.ClearHealthFeeForGFE;
    default:
      return null;
  }
}

// =============================================================================
// CLEAR PATIENT FEE CALCULATION HELPERS
// =============================================================================
// These helpers calculate the Clear Patient Fee with rounding to nearest $5.
// The fee is calculated as a percentage of the base amount, then rounded UP
// to the nearest $5 increment.
//
// Example: 3% of $1,670 = $50.10 → rounds to $55.00
//
// To revert to old (exact percentage) logic:
// 1. Uncomment the OLD LOGIC sections
// 2. Comment out the NEW LOGIC sections
// =============================================================================

/**
 * Round a dollar amount UP to the nearest $5 increment.
 * Examples: $50.10 → $55, $34.68 → $35, $350.00 → $350
 * @param amount - Amount in dollars
 * @returns Amount rounded up to nearest $5
 */
export const roundUpToNearest5Dollars = (amount: number): number => {
  if (amount <= 0) return 0;
  return Math.ceil(amount / 5) * 5;
};

/**
 * Calculate the Clear Patient Fee amount with rounding.
 * The fee is calculated as a percentage of the base amount, then rounded UP
 * to the nearest $5.
 *
 * @param baseAmount - Base amount in dollars (without fee)
 * @param percentage - Fee percentage (e.g., 3 for 3%)
 * @returns Fee amount in dollars, rounded up to nearest $5
 */
export const calculateClearPatientFee = (
  baseAmount: number,
  percentage: number | null | undefined
): number => {
  if (!percentage || percentage <= 0 || !baseAmount || baseAmount <= 0) {
    return 0;
  }

  // NEW LOGIC: Round up to nearest $5
  const exactFee = baseAmount * (percentage / 100);
  return roundUpToNearest5Dollars(exactFee);

  // OLD LOGIC (exact percentage, no rounding):
  // return baseAmount * (percentage / 100);
};

/**
 * Add Clear Patient Fee to a base amount.
 * Calculates the fee with rounding and adds it to the base amount.
 *
 * @param baseAmount - Base amount in dollars (without fee)
 * @param percentage - Fee percentage (e.g., 3 for 3%)
 * @returns Total amount in dollars (base + rounded fee)
 */
export const addClearPatientFee = (
  baseAmount: number,
  percentage: number | null | undefined
): number => {
  if (!percentage || percentage <= 0) {
    return baseAmount;
  }

  // NEW LOGIC: Add rounded fee
  const fee = calculateClearPatientFee(baseAmount, percentage);
  return baseAmount + fee;

  // OLD LOGIC (exact percentage):
  // return baseAmount * (1 + percentage / 100);
};

/**
 * Remove Clear Patient Fee from a total amount to get the base amount.
 * This reverses the addClearPatientFee calculation, accounting for rounding.
 *
 * Note: Due to rounding, we need to search for the correct base amount
 * rather than simply dividing.
 *
 * @param totalWithFee - Total amount in dollars (includes fee)
 * @param percentage - Fee percentage (e.g., 3 for 3%)
 * @returns Base amount in dollars (without fee)
 */
export const removeClearPatientFee = (
  totalWithFee: number,
  percentage: number | null | undefined
): number => {
  if (!percentage || percentage <= 0 || !totalWithFee || totalWithFee <= 0) {
    return totalWithFee || 0;
  }

  // NEW LOGIC: Search for base amount that produces this total when fee is added
  const p = percentage / 100;

  // Calculate the range of possible base values
  // Fee can add at most $4.99 extra due to rounding
  const maxBase = totalWithFee / (1 + p);
  const minBase = Math.max(0, (totalWithFee - 5) / (1 + p));

  // Try each cent value in the range (from high to low for efficiency)
  for (
    let baseCents = Math.floor(maxBase * 100);
    baseCents >= Math.floor(minBase * 100);
    baseCents -= 1
  ) {
    const base = baseCents / 100;
    const calculatedTotal = addClearPatientFee(base, percentage);
    if (Math.abs(calculatedTotal - totalWithFee) < 0.005) {
      return base;
    }
  }

  // Fallback to approximation (shouldn't reach here if totalWithFee was calculated correctly)
  return Math.round(maxBase * 100) / 100;

  // OLD LOGIC (exact percentage):
  // return totalWithFee / (1 + percentage / 100);
};

/**
 * Remove the Clear Patient Fee embedded in ONE SLICE of an order (a single
 * provider's portion, or a single provider's remaining-to-pay amount).
 *
 * This is NOT the same inverse as removeClearPatientFee(). The fee is rounded UP
 * to the nearest $5 exactly once, on the whole subtotal, and is then prorated
 * across the providers by their share of that subtotal — see
 * `distributeRoundedCPFForDisplay` in services/clear-api/src/domains/orders/order.helpers.ts.
 * A provider line therefore carries `share x roundedTotalFee` and is never itself
 * rounded to $5.
 *
 * Inverting a slice with removeClearPatientFee() re-applies the $5 round-up to the
 * slice and overstates the fee: a $200 portion inside a $1,200 subtotal at 4%
 * carries $8.33 of the rounded $50 fee, but removeClearPatientFee($208.33, 4)
 * finds a $10 fee and returns $198.33. Prorate with the same ratio instead.
 *
 * @param amountWithFee - Slice amount in dollars, fee included
 * @param percentage - Fee percentage (e.g. 4 for 4%)
 * @param subtotalInCents - The subtotal the fee was rounded up on and prorated
 *   across (sum of the provider portions, or of the remaining amounts)
 * @returns Slice amount in dollars with its prorated fee share removed
 */
export const removeProratedClearPatientFee = (
  amountWithFee: number,
  percentage: number | null | undefined,
  subtotalInCents: number | null | undefined
): number => {
  if (!percentage || percentage <= 0 || !amountWithFee || amountWithFee <= 0) {
    return amountWithFee || 0;
  }
  // Without the basis there is nothing to prorate against, so fall back to the
  // order-level inverse rather than silently skipping the fee.
  if (!subtotalInCents || subtotalInCents <= 0) {
    return removeClearPatientFee(amountWithFee, percentage);
  }

  const subtotal = subtotalInCents / 100;
  const totalWithFee = addClearPatientFee(subtotal, percentage);
  if (totalWithFee <= 0) return amountWithFee;

  return Math.round((amountWithFee * subtotal * 100) / totalWithFee) / 100;
};

/**
 * Get the fee multiplier for adding fee to a base amount.
 * This returns the multiplier that should be used with the base amount.
 *
 * NOTE: This function is DEPRECATED for new rounding logic.
 * Use addClearPatientFee() instead, as the multiplier approach
 * doesn't work correctly with rounding.
 *
 * @deprecated Use addClearPatientFee() instead
 * @param percentage - Fee percentage (e.g., 3 for 3%)
 * @returns Multiplier (e.g., 1.03 for 3%)
 */
export const getClearPatientFeeMultiplier = (
  percentage: number | null | undefined
): number => {
  if (!percentage || percentage <= 0) {
    return 1;
  }
  return 1 + percentage / 100;
};

/**
 * CLE-2798 — the credit-card surcharge disclosure, worded once for the checkout, the estimate PDF and
 * anything else that must state it. Ani (2026-09-16): "A 3% surcharge applies to credit card payments.
 * Debit and HSA/FSA card payments have no additional fee."
 */
export const creditCardSurchargeStatement = (percent: number): string =>
  `A ${percent}% surcharge applies to credit card payments. Debit and HSA/FSA card payments have no additional fee.`;
